import jwt_decode from 'jwt-decode';
import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useDarkMode } from './components/assets/DarkModeContext';
import Error403 from "./components/assets/Error403";
import Error404 from "./components/assets/Error404";
import Loader from "./components/assets/Loader";
import ResumePreview from "./components/edition-resume-preview/ResumePreview";
import Contact from "./components/footer/Contact";
import Footer from "./components/footer/Footer";
import Navbar from "./components/navbar/Navbar";
import { getProfile, updateLastLoginDate } from "./components/services/httpService";
import { AuthGuard } from "./components/SSO/AuthGuard";
import useAuth from "./components/SSO/useAuth";
import Information from "./components/user/Information";
import PdfContextProvider from "./context/pdf/pdf-context";
import ResumeContextProvider from "./context/resume/resume-context";
import Accueil from "./Pages/Accueil/Accueil";
import AppelOffre from "./Pages/Client/AppelOffre";
import Client from "./Pages/Client/Client";
import EspaceClient from "./Pages/Client/EspaceClient";
import { EditionResume } from "./Pages/EditionResume/EditionResume";
import Activation from "./Pages/Inscription/Activation";
import InscriptionCandidat from "./Pages/Inscription/InscriptionCandidat";
import InscriptionClient from "./Pages/Inscription/InscriptionClient";
import ResetPassword from "./Pages/Inscription/ResetPassword";
import CGU from "./Pages/Legales/CGU";
import Mentions from "./Pages/Legales/Mentions";
import Politique from "./Pages/Legales/Politique";
import Candidats from "./Pages/MesCV/Candidats";
import UserResumes from "./Pages/MesCV/UserResumes";
import MonCompte from "./Pages/MonCompte/MonCompte";
import MonEquipe from "./Pages/MonEquipe/MonEquipe";
import RechercheAvance from "./Pages/RechercheAvance/RechercheAvance";
import EnvoyerMessage from "./Pages/SuperAdmin/EnvoyerMessage";
import SuperAdmin from "./Pages/SuperAdmin/SuperAdmin";
import ValidationForm from "./Pages/SuperAdmin/ValidationForm";
import TestsTechnique from "./Pages/TestsTechnique/TestsTechnique";

export default function App() {
    const [ userProfile, setUserProfile ] = useState( null );
    const [ userRole, setUserRole ] = useState( null );
    const [ loading, setLoading ] = useState( true );
    const { isDarkMode } = useDarkMode();
    const auth = useAuth();

    useEffect( () => {
        const fetchProfileAndUpdateLoginDate = async () => {
            const token = localStorage.getItem( 'token' );
            if ( token ) {
                const decoded = jwt_decode( token );
                if ( decoded.exp < Date.now() / 1000 ) {
                    localStorage.removeItem( 'token' );
                    auth.displayLogin();
                } else {
                    try {
                        console.log("BEFORE getProfile")
                        const response = await getProfile();
                        console.log("AFTER getProfile")
                        const data = await response.json();
                        setUserProfile( data );
                        setUserRole( data.role );
                        await updateLastLoginDate( data.id ); // Await the async function
                    } catch ( error ) {
                        console.error( "Erreur lors de la récupération du profil :", error );
                        auth.logout();
                    }
                }
            } else {
                setLoading( false );
            }
        };

        fetchProfileAndUpdateLoginDate();
    }, [] );

    useEffect( () => {
        // Simule un délai de chargement de 2 secondes
        const timer = setTimeout( () => {
            setLoading( false );
        }, 2000 );

        return () => clearTimeout( timer );
    }, [] );

    useEffect( () => {
        const className = 'disable-transitions';
        if ( isDarkMode ) {
            document.body.classList.add( className );
            setTimeout( () => document.body.classList.remove( className ), 1000 );
        }
    }, [ isDarkMode ] );

    const PrivateRoute = ( { component: Component, allowedRoles, ...rest } ) => (
        <Route { ...rest } render={ ( props ) => {
            if ( loading ) {
                return <Loader/>;
            }

            if ( !auth || !allowedRoles.includes( userRole ) ) {
                return <Redirect to="/"/>;
            }

            return <Component { ...props } />;
        } }/>
    );

    return (
        <>
            { loading
                ? ( <Loader/> )
                : (
                    <div>
                        { auth ? <Navbar userProfile={ userProfile }/> : null }
                        <Switch>
                            <Route
                                exact path="/"
                                component={ Accueil }
                            />
                            {/* <PrivateRoute path="/inscription" component={Inscription} allowedRoles={['MANAGER', 'SUPERADMIN']} /> */ }
                            <Route
                                path="/inscription-candidat"
                                component={ InscriptionCandidat }
                            />
                            <Route
                                path="/inscription-client"
                                component={ InscriptionClient }
                            />
                            <Route
                                path="/activate/:token"
                                component={ Activation }
                            />
                            <Route
                                path="/reset-password/:token"
                                component={ ResetPassword }
                            />
                            <Route
                                path="/mon-compte"
                                component={ MonCompte }
                            />
                            <Route
                                path="/mes-informations"
                                rcomponent={ Information }
                            />
                            <PrivateRoute
                                path="/candidats"
                                component={ Candidats }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN' ] }
                            />
                            <PrivateRoute
                                path="/user-cvs/:userId"
                                component={ UserResumes }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN' ] }
                            />
                            <PrivateRoute
                                path="/mon-equipe"
                                component={ MonEquipe }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN' ] }
                            />
                            <PrivateRoute
                                path="/client"
                                component={ Client }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN' ] }
                            />
                            <PrivateRoute
                                path="/espace-client/id/:id"
                                component={ EspaceClient }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN', 'CLIENT' ] }
                            />
                            <PrivateRoute
                                path="/espace-client/name/:accountName"
                                component={ EspaceClient }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN', 'CLIENT' ] }
                            />
                            <PrivateRoute
                                path="/appel-offre/:id"
                                component={ AppelOffre }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN', 'CLIENT' ] }
                            />
                            <Route
                                path="/mentions-legales"
                                component={ Mentions }
                            />
                            <Route
                                path="/cgu"
                                component={ CGU }
                            />
                            <Route
                                path="/politique"
                                component={ Politique }
                            />
                            <Route
                                path="/aide"
                                component={ Contact }
                            />
                            <Route
                                path="/tests-techniques"
                                render={ () => (
                                    <AuthGuard>
                                        <TestsTechnique/>
                                    </AuthGuard>
                                ) }/>
                            <Route
                                path="/mon-espace-cv/:userId/:resumeId"
                                render={ () => (
                                        <PdfContextProvider>
                                            <ResumeContextProvider>
                                                <EditionResume/>
                                            </ResumeContextProvider>
                                        </PdfContextProvider>
                                ) }
                            />
                            <Route
                                path="/cv-preview/:userId/:resumeId"
                                render={ () => (
                                        <PdfContextProvider>
                                            <ResumeContextProvider>
                                                <div className='w-50 m-auto my-5'>
                                                    <ResumePreview/>
                                                </div>
                                            </ResumeContextProvider>
                                        </PdfContextProvider>
                                ) }
                            />
                            <PrivateRoute
                                path="/recherche-avance"
                                component={ RechercheAvance }
                                allowedRoles={ [ 'MANAGER', 'SUPERADMIN' ] }
                            />
                            <PrivateRoute
                                path="/super-admin"
                                component={ SuperAdmin }
                                allowedRoles={ [ 'SUPERADMIN' ] }
                            />
                            <Route
                                path="/validation/:token/:dataDuration?"
                                render={ ( props ) => <ValidationForm { ...props } user={ userProfile }/> }
                            />
                            <Route
                                path="/envoyer-message"
                                component={ EnvoyerMessage }
                            />
                            <Route
                                component={ Error404 }
                            />
                            <Route
                                path="/403"
                                component={ Error403 }
                            />
                        </Switch>
                        <Footer/>
                    </div>
                ) }
        </>
    );

}
